import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, ShieldCheck, MapPin, Tag, X, Info, Gauge, Plane, UserPlus, BadgeCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { DateField, DobField } from "@/components/DatePickers";
import { useLiveQuote } from "@/hooks/useLiveQuote";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { money } from "@/lib/format";
import { track } from "@/lib/analytics";
import RentalAgreementDialog from "@/components/booking/RentalAgreementDialog";
import VeriffVerification from "@/components/booking/VeriffVerification";

const todayStr = () => new Date().toISOString().split("T")[0];
const localTodayStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };

// Turo-style full booking widget embedded on the vehicle page.
export default function BookingPanel({ car, config, avail, initial }) {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const [form, setForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "", date_of_birth: "",
    pickup_date: initial?.pickup_date || todayStr(), return_date: initial?.return_date || "", pickup_location: initial?.pickup_location || "",
    refundable: true, notes: "", airport_dropoff: false, unlimited_miles: false,
    additional_driver_name: "", additional_driver_email: "",
  });
  const [addDriver, setAddDriver] = useState(false);
  const [verifyBookingId, setVerifyBookingId] = useState(null);
  const [discountInput, setDiscountInput] = useState("");
  const [appliedCode, setAppliedCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [agreementOpen, setAgreementOpen] = useState(false);

  useEffect(() => {
    // Restore a booking form saved before a sign-in redirect.
    const saved = sessionStorage.getItem(`belo_booking_${car.id}`);
    if (saved) {
      try {
        const f = JSON.parse(saved);
        setForm((prev) => ({ ...prev, ...f }));
        if (f.additional_driver_name || f.additional_driver_email) setAddDriver(true);
      } catch { /* corrupt — ignore */ }
      sessionStorage.removeItem(`belo_booking_${car.id}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [car.id]);

  useEffect(() => {
    if (car?.pickup_location) {
      setForm((f) => ({ ...f, pickup_location: car.pickup_location }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [car?.pickup_location]);

  useEffect(() => {
    if (user && user.role === "customer") {
      setForm((f) => ({ ...f, customer_name: f.customer_name || user.name || "", customer_email: f.customer_email || user.email || "" }));
    }
  }, [user]);

  const { quote, quoteError } = useLiveQuote({
    carId: car.id, form, appliedCode,
    onCodeError: (msg) => { toast.error(msg); setAppliedCode(""); },
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const minAdvance = config?.non_refundable_min_advance_days ?? 4;
  const advanceDays = form.pickup_date ? Math.round((new Date(form.pickup_date + "T00:00:00") - new Date(localTodayStr() + "T00:00:00")) / 86400000) : null;
  // Prefer the backend's authoritative eligibility (matches the charge); fall back to a local-date estimate before a quote exists.
  const nonRefEligible = quote ? !!quote.non_refundable_eligible : (advanceDays != null && advanceDays >= minAdvance);

  useEffect(() => {
    if (!nonRefEligible && !form.refundable) setForm((f) => ({ ...f, refundable: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonRefEligible]);

  const applyCode = () => { const c = discountInput.trim().toUpperCase(); if (c) setAppliedCode(c); };
  const removeCode = () => { setAppliedCode(""); setDiscountInput(""); };

  const overlapsBlocked = !!form.pickup_date && !!form.return_date &&
    (avail.blocked_ranges || []).some((r) => form.pickup_date <= r.end && r.start <= form.return_date);
  const carUnavailable = avail.available === false;
  const hasConflict = carUnavailable || overlapsBlocked;
  const canSubmit = !!quote && !quoteError && !hasConflict && !!form.pickup_location &&
    !!form.customer_name && !!form.customer_email && !!form.customer_phone;

  const maxDays = config?.max_rental_days ?? 92;
  const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const isBlockedDate = (d) => { const s = fmtDate(d); return (avail.blocked_ranges || []).some((r) => s >= r.start && s <= r.end); };
  const pickupObj = form.pickup_date ? new Date(form.pickup_date + "T00:00:00") : null;

  const reserve = (e) => {
    if (e) e.preventDefault();
    if (submitting) return;
    if (!quote || quoteError) { toast.error(quoteError || t("booking.completeRental")); return; }
    if (!canSubmit) { toast.info(t("booking.completeDetails")); return; }
    if (!user) {
      // Preserve form state so we return to a fully-populated booking panel
      // after the shopper finishes creating their account.
      sessionStorage.setItem(`belo_booking_${car.id}`, JSON.stringify(form));
      const redirectTo = window.location.pathname + window.location.search;
      const q = new URLSearchParams({
        redirect: redirectTo,
        name: form.customer_name,
        email: form.customer_email,
        phone: form.customer_phone,
      });
      if (form.date_of_birth) q.set("dob", form.date_of_birth);
      toast.info(t("veriff.createAccountToContinue"));
      navigate(`/register?${q.toString()}`);
      return;
    }
    if (addDriver && (!form.additional_driver_name.trim() || !form.additional_driver_email.trim())) {
      toast.error(t("veriff.driverIncomplete"));
      return;
    }
    setAgreementOpen(true);
  };

  const goCheckout = async (bookingId) => {
    try {
      const checkout = await api.post("/payments/checkout", { booking_id: bookingId, origin_url: window.location.origin });
      window.location.href = checkout.data.url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code === "identity_verification_required") {
        setAgreementOpen(false);
        setVerifyBookingId(bookingId);
        return;
      }
      if (detail?.code === "login_required") {
        sessionStorage.setItem(`belo_booking_${car.id}`, JSON.stringify(form));
        navigate(`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`);
        return;
      }
      toast.error(formatApiError(detail));
      setSubmitting(false);
      setAgreementOpen(false);
    }
  };

  const finalize = async (signatureName) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await api.post("/bookings", {
        car_id: car.id, customer_name: form.customer_name, customer_email: form.customer_email,
        customer_phone: form.customer_phone, date_of_birth: form.date_of_birth,
        pickup_date: form.pickup_date, return_date: form.return_date, pickup_location: form.pickup_location,
        refundable: form.refundable, discount_code: appliedCode, notes: form.notes, airport_dropoff: form.airport_dropoff,
        unlimited_miles: form.unlimited_miles, agreement_signed: true, signature_name: signatureName,
        additional_driver_name: addDriver ? form.additional_driver_name : "",
        additional_driver_email: addDriver ? form.additional_driver_email : "",
      });
      track("booking", { meta: { kind: "booking", car: car?.name } });
      if (!user?.identity_verified || user?.reverification_required) {
        setAgreementOpen(false);
        setSubmitting(false);
        setVerifyBookingId(res.data.id);
        return;
      }
      await goCheckout(res.data.id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setSubmitting(false);
      setAgreementOpen(false);
    }
  };

  const scrollToPanel = () => panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  const mobileReserve = () => { if (canSubmit) reserve(); else { scrollToPanel(); toast.info(t("booking.completeDetails")); } };

  const fromDaily = Math.round(car.rates?.daily_start || car.weekly_rate / 7);

  return (
    <>
      <div ref={panelRef} data-testid="booking-panel" className="bg-white rounded-2xl shadow-card border border-slate-100 p-6 sm:p-7">
        <div className="flex items-baseline justify-between">
          <div>
            <span className="font-heading text-2xl text-slate-900 font-bold">{quote ? money(quote.amount_due) : `$${fromDaily}`}</span>
            <span className="text-slate-500 text-sm ml-1.5">{quote ? t("booking.dueNow") : t("booking.perDayFrom")}</span>
          </div>
          <span className="text-slate-400 text-xs">{t("booking.inclDeposit", { deposit: car.deposit || config?.deposit || 200 })}</span>
        </div>

        <form onSubmit={reserve} data-testid="booking-form" className="mt-5 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("booking.pickup")}>
              <DateField value={form.pickup_date} onChange={(v) => setForm((f) => ({ ...f, pickup_date: v, return_date: f.return_date && f.return_date <= v ? "" : f.return_date }))} fromDate={startOfToday()} toDate={addDays(startOfToday(), maxDays)} disabled={isBlockedDate} placeholder={t("booking.pickupDate")} testid="input-pickup" />
            </Field>
            <Field label={t("booking.return")}>
              <DateField value={form.return_date} onChange={(v) => setForm((f) => ({ ...f, return_date: v }))} fromDate={pickupObj ? addDays(pickupObj, 1) : addDays(startOfToday(), 1)} toDate={addDays(pickupObj || startOfToday(), maxDays)} disabled={isBlockedDate} placeholder={t("booking.returnDate")} testid="input-return" />
            </Field>
          </div>

          <Field label={t("booking.dob")}>
            <DobField value={form.date_of_birth} onChange={(v) => setForm((f) => ({ ...f, date_of_birth: v }))} maxDate={startOfToday()} testid="input-dob" />
            {quote?.young_driver && <p data-testid="young-driver-note" className="text-amber-600 text-xs font-medium mt-1.5">{t("booking.youngDriverNote", { fee: config?.young_driver_fee || 75 })}</p>}
          </Field>

          <div>
            <span className="label-caps text-slate-400 mb-2 flex items-center gap-1.5"><MapPin size={12} /> {t("booking.pickupLocation")}</span>
            <div data-testid="pickup-location-fixed" className="flex items-start gap-3 p-3 rounded-xl border-2 border-blue-600 bg-blue-50">
              <MapPin size={16} className="text-blue-600 mt-0.5 shrink-0" />
              <span className="text-sm text-slate-700 font-medium leading-snug">{form.pickup_location || t("booking.pickupTbd")}
                {String(form.pickup_location || "").includes("Malden") && <span className="block text-slate-400 text-xs font-normal mt-0.5">{t("booking.milesFromLogan")}</span>}
              </span>
            </div>
          </div>

          <div>
            <span className="label-caps text-slate-400 block mb-2">{t("booking.cancellationPolicy")}</span>
            <div className="grid grid-cols-2 gap-2">
              <label data-testid="policy-refundable" className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${form.refundable ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
                <div className="flex items-center gap-2"><input type="radio" name="refundable" checked={form.refundable} onChange={() => setForm({ ...form, refundable: true })} className="w-4 h-4 accent-blue-600" /><span className="font-semibold text-slate-900 text-sm">{t("booking.refundable")}</span></div>
                <p className="text-slate-500 text-xs mt-1 ml-6">{t("booking.refundableDesc")}</p>
              </label>
              <label data-testid="policy-nonrefundable" className={`p-3 rounded-xl border-2 transition-all ${!nonRefEligible ? "border-slate-200 opacity-50 cursor-not-allowed" : !form.refundable ? "border-blue-600 bg-blue-50 cursor-pointer" : "border-slate-200 hover:border-slate-300 cursor-pointer"}`}>
                <div className="flex items-center justify-between gap-1">
                  <div className="flex items-center gap-2"><input type="radio" name="refundable" disabled={!nonRefEligible} checked={!form.refundable} onChange={() => nonRefEligible && setForm({ ...form, refundable: false })} className="w-4 h-4 accent-blue-600" /><span className="font-semibold text-slate-900 text-sm">{t("booking.nonRefundable")}</span></div>
                  <span className="text-green-700 bg-green-100 text-[10px] font-bold px-1.5 py-0.5 rounded-full">{t("booking.off10")}</span>
                </div>
                {nonRefEligible
                  ? <p className="text-slate-500 text-xs mt-1 ml-6">{t("booking.nonRefDesc")}</p>
                  : <p data-testid="nonref-ineligible-note" className="text-amber-600 text-xs mt-1 ml-6">{t("booking.nonRefIneligible", { days: minAdvance })}</p>}
              </label>
            </div>
          </div>

          <label data-testid="unlimited-option" className={`flex items-start gap-3 p-3.5 rounded-xl border-2 cursor-pointer transition-all ${form.unlimited_miles ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
            <input type="checkbox" checked={form.unlimited_miles} onChange={(e) => setForm({ ...form, unlimited_miles: e.target.checked })} data-testid="input-unlimited" className="mt-0.5 w-5 h-5 accent-blue-600" />
            <span>
              <span className="flex items-center gap-2 font-semibold text-slate-900 text-sm"><Gauge size={15} className="text-blue-600" /> {t("booking.unlimitedMiles")} <span className="text-blue-600">+${config?.unlimited_miles_per_day || 10}{t("booking.perDay")}</span></span>
              <span className="block text-slate-500 text-xs mt-0.5">{t("booking.unlimitedMilesDesc", { miles: config?.miles_per_day || 150 })}</span>
            </span>
          </label>

          {car.offers_airport_service && (
            <label data-testid="airport-option" className={`flex items-start gap-3 p-3.5 rounded-xl border-2 cursor-pointer transition-all ${form.airport_dropoff ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
              <input type="checkbox" checked={form.airport_dropoff} onChange={(e) => setForm({ ...form, airport_dropoff: e.target.checked })} data-testid="input-airport" className="mt-0.5 w-5 h-5 accent-blue-600" />
              <span>
                <span className="flex items-center gap-2 font-semibold text-slate-900 text-sm"><Plane size={15} className="text-blue-600" /> Logan Airport pickup &amp; return {car.airport_fee > 0 && <span className="text-blue-600">+{money(car.airport_fee)}</span>}</span>
                <span className="block text-slate-500 text-xs mt-0.5">Meet your host at Boston Logan (BOS) — skip the counter lines.</span>
              </span>
            </label>
          )}

          <div>
            <span className="label-caps text-slate-400 mb-2 flex items-center gap-1.5"><Tag size={12} /> {t("booking.discountCode")}</span>
            {appliedCode && quote?.discount_label ? (
              <div data-testid="applied-code" className="flex items-center justify-between bg-green-50 border-2 border-green-200 rounded-xl px-3.5 py-2.5">
                <span className="text-green-700 font-semibold text-sm">{appliedCode} — {quote.discount_label}</span>
                <button type="button" onClick={removeCode} data-testid="remove-code" className="text-green-700 hover:text-green-900"><X size={15} /></button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input value={discountInput} onChange={(e) => setDiscountInput(e.target.value)} data-testid="input-discount" className="belo-input uppercase" placeholder={t("booking.enterCode")} />
                <button type="button" onClick={applyCode} data-testid="apply-discount" className="shrink-0 bg-slate-900 text-white px-5 rounded-xl text-sm font-semibold hover:bg-slate-800 transition-colors">{t("booking.apply")}</button>
              </div>
            )}
          </div>

          <div className="pt-1 border-t border-slate-100">
            <span className="label-caps text-slate-400 block mb-3 mt-4">{t("booking.yourDetails")}</span>
            <div className="space-y-3">
              <Field label={t("booking.fullName")}><input required value={form.customer_name} onChange={set("customer_name")} data-testid="input-name" className="belo-input" placeholder="John Doe" /></Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label={t("booking.email")}><input required type="email" value={form.customer_email} onChange={set("customer_email")} data-testid="input-email" className="belo-input" placeholder="you@email.com" /></Field>
                <Field label={t("booking.phone")}><input required value={form.customer_phone} onChange={set("customer_phone")} data-testid="input-phone" className="belo-input" placeholder="(978) 000-0000" /></Field>
              </div>
              <Field label={t("booking.notesOptional")}><textarea value={form.notes} onChange={set("notes")} data-testid="input-notes" rows={2} className="belo-input resize-none" placeholder={t("booking.notesPlaceholder")} /></Field>
            </div>
          </div>

          <div className="pt-1 border-t border-slate-100">
            <label data-testid="toggle-additional-driver" className="flex items-center gap-3 mt-4 cursor-pointer">
              <input type="checkbox" checked={addDriver} onChange={(e) => setAddDriver(e.target.checked)} className="w-5 h-5 accent-blue-600" />
              <span className="flex items-center gap-2 font-semibold text-slate-900 text-sm"><UserPlus size={15} className="text-blue-600" /> {t("veriff.addDriver")}</span>
            </label>
            {addDriver && (
              <div className="mt-3 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label={t("veriff.driverName")}><input value={form.additional_driver_name} onChange={set("additional_driver_name")} data-testid="input-driver-name" className="belo-input" placeholder="Jane Doe" /></Field>
                  <Field label={t("veriff.driverEmail")}><input type="email" value={form.additional_driver_email} onChange={set("additional_driver_email")} data-testid="input-driver-email" className="belo-input" placeholder="driver@email.com" /></Field>
                </div>
                <p className="text-slate-400 text-xs">{t("veriff.additionalDriverNote")}</p>
              </div>
            )}
          </div>

          {hasConflict && (
            <div data-testid="availability-warning" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              {carUnavailable ? t("booking.unavailableWarning") : t("booking.overlapWarning")}
            </div>
          )}
          {quoteError && <div data-testid="quote-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{quoteError}</div>}

          <PriceSummary quote={quote} />

          <button type="submit" disabled={submitting || !canSubmit} data-testid="submit-booking" className="w-full bg-blue-600 text-white py-4 rounded-full font-semibold hover:bg-blue-700 transition-all hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0">
            {submitting ? t("booking.redirecting") : hasConflict ? t("carCard.unavailable") : quote ? t("booking.reservePay", { amount: money(quote.amount_due) }) : t("booking.reservePayShort")}
          </button>
          <p className="flex items-center justify-center gap-2 text-xs text-slate-400"><ShieldCheck size={14} /> {t("booking.securePayment", { deposit: car.deposit || config?.deposit || 200 })}</p>
          {user?.identity_verified && !user?.reverification_required ? (
            <p data-testid="identity-verified-chip" className="flex items-center justify-center gap-1.5 text-xs font-semibold text-green-700"><BadgeCheck size={14} /> {t("veriff.verifiedBadge")}</p>
          ) : (
            <p data-testid="identity-step-note" className="flex items-center justify-center gap-1.5 text-xs text-slate-400"><ShieldCheck size={13} /> {t("veriff.stepNote")}</p>
          )}
        </form>
      </div>

      {/* Mobile sticky action bar */}
      <div data-testid="mobile-reserve-bar" className="fixed bottom-0 inset-x-0 z-40 xl:hidden bg-white/95 backdrop-blur border-t border-slate-200 px-5 py-3 flex items-center justify-between shadow-[0_-4px_20px_rgba(0,0,0,0.06)]">
        <div>
          <p className="font-heading text-lg text-slate-900 font-bold leading-none">{quote ? money(quote.amount_due) : `$${fromDaily}`}</p>
          <p className="text-slate-400 text-xs mt-0.5">{quote ? t("booking.dueNow") : t("booking.perDayFrom")}</p>
        </div>
        <button type="button" onClick={mobileReserve} disabled={submitting} data-testid="mobile-reserve-btn" className="bg-blue-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50">
          {canSubmit ? t("booking.reservePayShort") : t("booking.reserve")}
        </button>
      </div>

      <RentalAgreementDialog open={agreementOpen} onOpenChange={setAgreementOpen} car={car} form={form} quote={quote} onSign={finalize} submitting={submitting} />

      {verifyBookingId && (
        <VeriffVerification
          createSession={() => api.post("/veriff/session").then((r) => r.data)}
          onVerified={async () => {
            try { await refresh(); } catch { /* keep going — backend re-checks anyway */ }
            const id = verifyBookingId;
            setVerifyBookingId(null);
            setSubmitting(true);
            await goCheckout(id);
          }}
          onClose={() => { setVerifyBookingId(null); setSubmitting(false); }}
        />
      )}
    </>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="label-caps text-slate-400 block mb-1.5">{label}</span>
    {children}
  </label>
);

function PriceSummary({ quote }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (!quote)
    return <p data-testid="summary-placeholder" className="text-slate-400 text-sm bg-slate-50 rounded-xl p-4 text-center">{t("booking.summaryPlaceholder")}</p>;
  const tripCost = Math.round((quote.amount_due - quote.deposit) * 100) / 100;
  return (
    <div className="bg-slate-50 rounded-xl p-4 sm:p-5 space-y-2.5">
      <div className="flex justify-between text-sm"><span className="text-slate-500 flex items-center gap-1.5"><CalendarDays size={14} /> {t("booking.duration")}</span><span className="text-slate-900 font-medium">{t("common.days", { count: quote.days })}</span></div>
      <div className="flex justify-between text-sm"><span className="text-slate-500 flex items-center gap-1.5"><Gauge size={14} /> {t("booking.milesIncluded")}</span><span className="text-slate-900 font-medium">{quote.unlimited_miles ? t("booking.unlimited") : `${(quote.included_miles || 0).toLocaleString()} mi`}</span></div>
      <div className="flex justify-between text-sm items-center">
        <HoverCard open={open} onOpenChange={setOpen} openDelay={60} closeDelay={250}>
          <HoverCardTrigger asChild>
            <button type="button" data-testid="rental-breakdown-trigger" onClick={() => setOpen((o) => !o)} className="text-slate-500 flex items-center gap-1 border-b border-dashed border-slate-300 hover:text-slate-900 transition-colors cursor-help">
              {t("booking.rental")} <Info size={13} className="text-slate-400" />
            </button>
          </HoverCardTrigger>
          <HoverCardContent align="start" className="w-60" data-testid="rental-breakdown-content">
            <p className="label-caps text-slate-400 mb-2">{t("booking.rentalBreakdown")}</p>
            <div className="space-y-1.5">
              {(quote.rental_breakdown || []).map((line) => (
                <div key={line.label} className="flex justify-between text-sm"><span className="text-slate-600">{line.label}</span><span className="text-slate-900 font-medium">{money(line.amount)}</span></div>
              ))}
              <div className="flex justify-between text-sm pt-1.5 border-t border-slate-100"><span className="text-slate-500">{t("booking.subtotal", { count: quote.days })}</span><span className="text-slate-900 font-semibold">{money(quote.rental_base)}</span></div>
            </div>
          </HoverCardContent>
        </HoverCard>
        <span className="text-slate-900 font-medium">{money(quote.rental_base)}</span>
      </div>
      {quote.non_refundable_discount > 0 && <div className="flex justify-between text-sm text-green-700"><span>{t("booking.nonRefDiscount")}</span><span className="font-medium">−{money(quote.non_refundable_discount)}</span></div>}
      {quote.service_fee > 0 && <div className="flex justify-between text-sm"><span className="text-slate-500">{t("booking.serviceFee", { pct: Math.round((quote.service_fee_rate || 0.1) * 100) })}</span><span className="text-slate-900 font-medium">{money(quote.service_fee)}</span></div>}
      <div className="flex justify-between text-sm"><span className="text-slate-500">{t("booking.tax")}</span><span className="text-slate-900 font-medium">{money(quote.tax)}</span></div>
      {quote.young_driver_fee > 0 && <div className="flex justify-between text-sm"><span className="text-slate-500">{t("booking.youngDriverFee")}</span><span className="text-slate-900 font-medium">{money(quote.young_driver_fee)}</span></div>}
      {quote.airport_fee > 0 && <div className="flex justify-between text-sm text-blue-700"><span className="flex items-center gap-2"><MapPin size={14} /> {t("booking.airportDropoff")}</span><span className="font-medium">{money(quote.airport_fee)}</span></div>}
      {quote.unlimited_miles_fee > 0 && <div className="flex justify-between text-sm text-blue-700"><span className="flex items-center gap-2"><Gauge size={14} /> {t("booking.unlimitedMiles")}</span><span className="font-medium">{money(quote.unlimited_miles_fee)}</span></div>}
      {quote.discount_amount > 0 && <div className="flex justify-between text-sm text-green-700"><span>{t("booking.discount", { code: quote.discount_code })}</span><span className="font-medium">−{money(quote.discount_amount)}</span></div>}
      <div className="flex justify-between items-baseline pt-3 border-t border-slate-200">
        <span className="text-slate-900 font-semibold">{t("booking.tripCost")} <span className="text-slate-400 font-normal text-xs">{t("booking.tripCostNote")}</span></span>
        <span data-testid="trip-cost" className="text-slate-900 font-bold text-base">{money(tripCost)}</span>
      </div>
      <div data-testid="deposit-row" className="flex items-center justify-between rounded-xl bg-slate-100 border border-dashed border-slate-300 px-3.5 py-2.5 mt-2.5">
        <span className="flex items-center gap-1.5 text-slate-500 text-sm"><ShieldCheck size={14} className="text-slate-400" /> {t("booking.refundableDepositRow")}</span>
        <span className="text-slate-500 text-sm font-medium">{money(quote.deposit)}</span>
      </div>
      <p data-testid="deposit-note" className="text-slate-400 text-xs px-1 -mt-0.5">{t("booking.depositNote")}</p>
      <div className="flex justify-between items-center pt-3 border-t border-slate-200">
        <span className="text-slate-900 font-semibold">{quote.payment_plan === "split" ? "Due today" : t("booking.payNow")}</span>
        <span data-testid="amount-due-now" className="font-heading text-2xl text-blue-700 font-bold">{money(quote.amount_due)}</span>
      </div>
      {quote.payment_plan === "split" && (
        <div data-testid="split-plan" className="mt-3 rounded-xl bg-blue-50 border border-blue-100 p-4">
          <p className="flex items-center gap-2 text-blue-800 font-semibold text-sm"><CalendarDays size={15} /> Weekly payment plan</p>
          <p className="text-slate-600 text-xs mt-1.5 leading-relaxed">
            For long-term rentals you pay <b>{money(quote.due_now)}</b> today (first week + fees, taxes &amp; the {money(quote.deposit)} refundable deposit). We then automatically charge <b>{money(quote.weekly_installment)}</b> every 7 days for the remaining {(quote.installments_count || 1) - 1} {quote.installments_count === 2 ? "week" : "weeks"}.
          </p>
          <div className="flex justify-between text-sm mt-3 pt-3 border-t border-blue-100">
            <span className="text-slate-500">Total over {quote.days} days</span>
            <span data-testid="split-total" className="text-slate-900 font-semibold">{money(quote.total_due)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
